import { Box, Typography, Stack, Skeleton } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { queryItemTypesWithLocations, ItemType, ItemTypeWithLocations } from '../schema';
import ItemTypeRow from './ItemTypeRow';
import InventorySearchBar, { SortOption } from './InventorySearchBar';
import ListEmptyState from './ListEmptyState';
import { calculateItemLocations, LocationData } from '../utils/inventory';
import { filterValidItemLocations } from '../utils/instantDbGuards';

interface Props {
    onEdit?: (itemType: ItemType) => void;
    onDelete?: (itemType: ItemType) => void;
    onViewDetails?: (itemType: ItemType) => void;
    onRecordPurchase?: (itemType: ItemType) => void;
    onRecordLostOrBroken?: (itemType: ItemType) => void;
    onRecordConsumption?: (itemType: ItemType) => void;
    onAdjustQuantity?: (itemType: ItemType) => void;
    /** When provided, empty state shows "Add Item" CTA. */
    onAddItem?: () => void;
}

export default function GlobalInventoryList({
    onEdit,
    onDelete,
    onViewDetails,
    onRecordPurchase,
    onRecordLostOrBroken,
    onRecordConsumption,
    onAdjustQuantity,
    onAddItem,
}: Props) {
    const { data, isLoading, error } = queryItemTypesWithLocations();
    const [searchParams, setSearchParams] = useSearchParams();

    const searchText = searchParams.get('search') || '';
    const categoryId = searchParams.get('category') || null;
    const locationId = searchParams.get('location') || null;
    const consumableOnly = searchParams.get('consumableOnly') === 'true';
    const sortBy = (searchParams.get('sort') || 'name-asc') as SortOption;

    const handleSearchChange = (text: string) => {
        if (text) {
            searchParams.set('search', text);
        } else {
            searchParams.delete('search');
        }
        setSearchParams(searchParams);
    };

    const handleCategoryChange = (id: string | null) => {
        if (id) {
            searchParams.set('category', id);
        } else {
            searchParams.delete('category');
        }
        setSearchParams(searchParams);
    };

    const handleLocationChange = (id: string | null) => {
        if (id) {
            searchParams.set('location', id);
        } else {
            searchParams.delete('location');
        }
        setSearchParams(searchParams);
    };

    const handleSortChange = (sort: SortOption) => {
        searchParams.set('sort', sort);
        setSearchParams(searchParams);
    };

    const handleConsumableOnlyChange = (consumableOnly: boolean) => {
        if (consumableOnly) {
            searchParams.set('consumableOnly', 'true');
        } else {
            searchParams.delete('consumableOnly');
        }
        setSearchParams(searchParams);
    };

    // Calculate location data for all items
    const locationDataMap = useMemo(() => {
        const map = new Map<string, LocationData>();
        const itemTypes = (data?.itemTypes || []) as ItemTypeWithLocations[];
        for (const itemType of itemTypes) {
            const locations = filterValidItemLocations(
                (itemType.itemLocations || []) as Array<{
                    id: string;
                    quantity: number;
                    event: { id: string; name: string } | null | undefined;
                    storageLocation?: { id: string; name: string } | null;
                }>
            );
            const locationData = calculateItemLocations(itemType, locations);
            map.set(itemType.id, locationData);
        }
        return map;
    }, [data]);

    // Filter and sort items
    const filteredAndSortedItems = useMemo(() => {
        let items = data?.itemTypes || [];

        // Filter by search text
        if (searchText) {
            const lowerSearch = searchText.toLowerCase();
            items = items.filter(
                item =>
                    item.name.toLowerCase().includes(lowerSearch) ||
                    (item.description && item.description.toLowerCase().includes(lowerSearch))
            );
        }

        // Filter by category
        if (categoryId) {
            items = items.filter(item => item.category?.id === categoryId);
        }

        // Filter by location
        if (locationId) {
            items = items.filter(item => {
                const locationData = locationDataMap.get(item.id);
                if (!locationData) return false;
                return (
                    locationData.storageLocations.some(s => s.id === locationId && s.quantity > 0) ||
                    locationData.events.some(e => e.eventId === locationId)
                );
            });
        }

        // Filter by consumable
        if (consumableOnly) {
            items = items.filter(item => item.consumable === true);
        }

        // Sort
        items = [...items].sort((a, b) => {
            switch (sortBy) {
                case 'name-asc':
                    return (a.name || '').localeCompare(b.name || '');
                case 'name-desc':
                    return (b.name || '').localeCompare(a.name || '');
                case 'category': {
                    const aCat = a.category?.name || '';
                    const bCat = b.category?.name || '';
                    if (aCat !== bCat) return aCat.localeCompare(bCat);
                    return (a.name || '').localeCompare(b.name || '');
                }
                case 'quantity-high': {
                    const aTotal = locationDataMap.get(a.id)?.total || 0;
                    const bTotal = locationDataMap.get(b.id)?.total || 0;
                    return bTotal - aTotal;
                }
                case 'quantity-low': {
                    const aTotal = locationDataMap.get(a.id)?.total || 0;
                    const bTotal = locationDataMap.get(b.id)?.total || 0;
                    return aTotal - bTotal;
                }
                default:
                    return 0;
            }
        });

        return items;
    }, [data, searchText, categoryId, locationId, consumableOnly, sortBy, locationDataMap]);

    if (isLoading) {
        return (
            <Stack spacing={2}>
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rectangular" height={80} />
                ))}
            </Stack>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="error">
                    Error loading items: {error.message}
                </Typography>
            </Box>
        );
    }

    const hasActiveFilters = searchText || categoryId || locationId || sortBy !== 'name-asc';
    const hasItems = (data?.itemTypes || []).length > 0;

    return (
        <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
            <InventorySearchBar
                searchText={searchText}
                categoryId={categoryId}
                locationId={locationId}
                consumableOnly={consumableOnly}
                sortBy={sortBy}
                onSearchChange={handleSearchChange}
                onCategoryChange={handleCategoryChange}
                onLocationChange={handleLocationChange}
                onConsumableOnlyChange={handleConsumableOnlyChange}
                onSortChange={handleSortChange}
                locationDataMap={locationDataMap}
            />
            {filteredAndSortedItems.length === 0 ? (
                hasActiveFilters ? (
                    <ListEmptyState
                        icon={<Inventory2Icon sx={{ fontSize: 48 }} />}
                        message="No items match your filters"
                        subtext="Try adjusting your search or filters"
                    />
                ) : (
                    <ListEmptyState
                        icon={<Inventory2Icon sx={{ fontSize: 48 }} />}
                        message={hasItems ? 'No items' : 'No items in inventory yet'}
                        subtext={
                            hasItems
                                ? 'Click the + button to add a new item'
                                : 'Add your first item to start tracking equipment and supplies'
                        }
                        primaryAction={onAddItem ? { label: 'Add Item', onClick: onAddItem } : undefined}
                    />
                )
            ) : (
                <Stack spacing={1}>
                    {filteredAndSortedItems.map(itemType => (
                        <ItemTypeRow
                            key={itemType.id}
                            itemType={itemType}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onViewDetails={onViewDetails}
                            onRecordPurchase={onRecordPurchase}
                            onRecordLostOrBroken={onRecordLostOrBroken}
                            onRecordConsumption={onRecordConsumption}
                            onAdjustQuantity={onAdjustQuantity}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
